import * as Astronomy from 'astronomy-engine';
import { positionsAtDate } from '../../astro/ephemeris.ts';
import { placementOf } from '../../astro/zodiac.ts';
import { confident, precisionToMinutes } from '../../astro/uncertainty.ts';
import { resolveBirthTime, resolveWithOffset } from '../../astro/time.ts';
import {
  COLOR_CONVENTION,
  PLANET_COLORS,
  WEEKDAY_LORDS,
  nakshatraLord,
  type Planet,
} from '../../astro/tables/colors.ts';
import { readBirthDetails, renderTimeProblem, type BirthDetails } from './birth-details.ts';
import { reportToolComplete, reportToolStart } from './analytics.ts';

/**
 * Lucky Colour Finder.
 *
 * DESIGN_SPEC §6.4 asks for date of birth only. That is workable, but only via
 * the weekday (Vara) lord — the Moon changes nakshatra roughly daily, so a date
 * alone cannot give a birth nakshatra. Rather than quietly pick one basis and
 * present it as the other, the tool runs in two clearly-labelled modes and
 * always states which it used:
 *
 *   date only            -> Vara (weekday) lord
 *   date + time + place  -> janma nakshatra lord, via Vimshottari
 *
 * The date-only path carries a real subtlety: the Vedic day begins at SUNRISE,
 * so someone born at 04:00 belongs to the *previous* weekday's lord. With no
 * time we cannot know, so we say so and offer the toggle. With a time and place
 * we compute the actual sunrise and resolve it properly.
 */

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K, className?: string, text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
}

/** Sunrise for the given local date and place, as a UTC instant. */
function sunriseFor(utcNearDate: Date, latitude: number, longitude: number): Date | null {
  const observer = new Astronomy.Observer(latitude, longitude, 0);
  // Search from 18 hours before, so the sunrise bounding the birth is found.
  const start = Astronomy.MakeTime(new Date(utcNearDate.getTime() - 18 * 3600_000));
  const found = Astronomy.SearchRiseSet(Astronomy.Body.Sun, observer, +1, start, 2);
  return found ? found.date : null;
}

interface Result {
  planet: Planet;
  basis: 'nakshatra' | 'weekday';
  detail: string;
  /** Set when the weekday itself is ambiguous without a birth time. */
  alternative?: { planet: Planet; note: string };
}

function renderSwatch(swatch: { name: string; hex: string }, size: 'lg' | 'sm'): HTMLElement {
  const wrap = el('div', `color-swatch color-swatch--${size}`);
  const disc = el('span', 'color-swatch__disc');
  disc.style.background = swatch.hex;
  wrap.appendChild(disc);
  wrap.appendChild(el('p', 'color-swatch__name', swatch.name));
  wrap.appendChild(el('p', 'color-swatch__hex', swatch.hex.toUpperCase()));
  return wrap;
}

function renderResult(container: HTMLElement, result: Result): void {
  const entry = PLANET_COLORS[result.planet];
  container.replaceChildren();

  const head = el('div', 'chart-result__head');
  head.appendChild(el('p', 'tool-result__where',
    result.basis === 'nakshatra' ? 'From your birth nakshatra' : 'From your birth weekday'));
  head.appendChild(el('p', 'chart-result__lagna', `${result.planet} · ${entry.sanskrit}`));
  head.appendChild(el('p', 'tool-result__element', result.detail));
  container.appendChild(head);

  if (result.alternative) {
    const alt = el('p', 'tool-caveat');
    alt.textContent = result.alternative.note;
    container.appendChild(alt);
  }

  const swatches = el('div', 'color-swatches');
  swatches.appendChild(renderSwatch(entry.primary, 'lg'));
  swatches.appendChild(renderSwatch(entry.secondary, 'sm'));
  container.appendChild(swatches);

  container.appendChild(el('p', 'tool-result__principle', entry.rationale));

  container.appendChild(el('h4', 'tool-result__subhead', 'Where to use them'));
  const list = el('ul', 'tool-result__remedies');
  for (const use of entry.uses) list.appendChild(el('li', undefined, use));
  container.appendChild(list);

  const method = el('details', 'tool-method');
  method.appendChild(el('summary', undefined, 'How this is worked out'));
  method.appendChild(el('p', undefined, COLOR_CONVENTION));
  container.appendChild(method);

  container.hidden = false;
  requestAnimationFrame(() => container.classList.add('is-revealed'));

  /* Only reached with a real colour entry rendered. The time-problem branch in
     the submit handler returns before ever calling this. */
  reportToolComplete('lucky-colors');
}

/** Weekday lord, accounting for the Vedic day starting at sunrise. */
function weekdayResult(details: BirthDetails, utc: Date | null): Result {
  const { year, month, day } = details.wall;
  const localNoon = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  const dayIndex = localNoon.getUTCDay();
  const planet = WEEKDAY_LORDS[dayIndex]!;
  const prevPlanet = WEEKDAY_LORDS[(dayIndex + 6) % 7]!;

  const dayName = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][dayIndex]!;

  // With a time and place we can settle the sunrise question outright.
  if (utc && details.precision !== 'unknown') {
    const sunrise = sunriseFor(utc, details.latitude, details.longitude);
    if (sunrise && utc.getTime() < sunrise.getTime()) {
      return {
        planet: prevPlanet,
        basis: 'weekday',
        detail: `Born before sunrise on ${dayName}, so the Vedic day is still the previous one.`,
      };
    }
    return { planet, basis: 'weekday', detail: `Born on ${dayName}, after sunrise.` };
  }

  return {
    planet,
    basis: 'weekday',
    detail: `Born on a ${dayName}.`,
    alternative: {
      planet: prevPlanet,
      note:
        `The Vedic day begins at sunrise, not midnight. If you were born before dawn, your ruling ` +
        `planet is ${prevPlanet} instead of ${planet}. Add a birth time and place above and we can ` +
        `settle it exactly.`,
    },
  };
}

export function initLuckyColorsTool(): void {
  const form = document.getElementById('colors-form') as HTMLFormElement | null;
  if (!form) return;
  const output = document.getElementById('colors-output');
  const skeleton = document.getElementById('colors-skeleton');
  if (!output || !skeleton) return;

  let started = false;
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    /* The weekday basis needs no coordinates, so a date-only submission is
       valid here. The nakshatra basis below only runs when a place was given. */
    const details = readBirthDetails(form, { placeOptional: true });
    if (!details) return;
    if (!started) { started = true; reportToolStart('lucky-colors'); }

    output.hidden = true;
    output.classList.remove('is-revealed');
    skeleton.hidden = false;

    window.setTimeout(() => {
      skeleton.hidden = true;
      output.replaceChildren();

      /* Without a time — or without a place — the nakshatra basis is simply
         unavailable, so use the weekday lord and label it as such. */
      if (details.precision === 'unknown' || !details.hasPlace) {
        renderResult(output, weekdayResult(details, null));
        return;
      }

      const resolved = resolveBirthTime(details.wall, details.timezone, details.longitude);
      if (!resolved.ok) {
        output.appendChild(renderTimeProblem(resolved));
        output.hidden = false;
        requestAnimationFrame(() => output.classList.add('is-revealed'));
        return;
      }

      const utc = resolved.utc;
      const place = { latitude: details.latitude, longitude: details.longitude };
      const delta = precisionToMinutes(details.precision);

      // Prefer the nakshatra basis, but only when the birth time can pin it.
      const gate = confident(
        (t) => placementOf(positionsAtDate(t, place).longitudes.Moon).nakshatraIndex,
        utc, delta, (v) => v,
      );

      if (gate.certain) {
        const p = placementOf(positionsAtDate(utc, place).longitudes.Moon);
        renderResult(output, {
          planet: nakshatraLord(gate.value),
          basis: 'nakshatra',
          detail: `Moon in ${p.nakshatra}, ruled by ${nakshatraLord(gate.value)}.`,
        });
        return;
      }

      // Moon sits on a nakshatra boundary — fall back rather than guess.
      const fallback = weekdayResult(details, utc);
      fallback.alternative = {
        planet: fallback.planet,
        note:
          'Your Moon sits close to a nakshatra boundary, so we cannot be sure which one it falls ' +
          'in with the birth time you gave. We have used your birth weekday instead, which does ' +
          'not depend on the exact minute.',
      };
      renderResult(output, fallback);
    }, 500);
  });
}
